import { faEllipsisVertical } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { createContext, type ReactNode, useContext } from "react";
import { createPortal } from "react-dom";

import { useDropdownState } from "./useDropdownState";

interface DSActionMenuContextValue {
  close: () => void;
}

const DSActionMenuContext = createContext<DSActionMenuContextValue | null>(
  null,
);

const DEFAULT_TRIGGER = (
  <FontAwesomeIcon icon={faEllipsisVertical} className="h-4 w-4" />
);

export interface DSActionMenuProps {
  dataTestId: string;
  /** Defaults to "More actions". */
  ariaLabel?: string;
  /**
   * Glyph rendered inside the 32x32 trigger. Defaults to the kebab icon.
   * Pass any ReactNode — a `<FontAwesomeIcon>`, emoji `<span>`, etc.
   */
  triggerContent?: ReactNode;
  /** Disables the trigger; the menu cannot be opened. */
  disabled?: boolean;
  /** Native `title` on the trigger — handy for "why is this disabled" hints. */
  title?: string;
  children: ReactNode;
}

/**
 * Kebab / overflow menu for row or card actions. 32x32 icon trigger;
 * menu portals to body so `overflow-x: auto` parents don't clip it.
 * Pass `triggerContent` to swap the trigger glyph (e.g. theme picker).
 */
export function DSActionMenu({
  dataTestId,
  ariaLabel = "More actions",
  triggerContent = DEFAULT_TRIGGER,
  disabled = false,
  title,
  children,
}: DSActionMenuProps) {
  const { isOpen, setIsOpen, triggerRef, menuRef, triggerRect } =
    useDropdownState<HTMLButtonElement>();

  const triggerClass = disabled
    ? "inline-flex h-8 w-8 items-center justify-center rounded-standard text-placeholder cursor-not-allowed"
    : "inline-flex h-8 w-8 items-center justify-center rounded-standard text-brand hover:bg-tertiary hover:text-brand-dark";

  return (
    <DSActionMenuContext.Provider value={{ close: () => setIsOpen(false) }}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          if (disabled) return;
          setIsOpen(!isOpen);
        }}
        disabled={disabled}
        title={title}
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        data-testid={dataTestId}
        className={triggerClass}
      >
        {triggerContent}
      </button>
      {!disabled &&
        isOpen &&
        triggerRect &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            className="fixed z-dropdown min-w-[200px] overflow-hidden rounded-standard bg-input shadow-[0px_0px_10px_0px_var(--shadow-primary)]"
            style={{
              top: triggerRect.bottom + 4,
              right: window.innerWidth - triggerRect.right,
            }}
          >
            {children}
          </div>,
          document.body,
        )}
    </DSActionMenuContext.Provider>
  );
}

export interface DSActionMenuItemProps {
  onSelect: () => void;
  destructive?: boolean;
  disabled?: boolean;
  /** When true, highlights the row to indicate the current selection. */
  active?: boolean;
  dataTestId?: string;
  children: ReactNode;
}

/** A single row inside `<DSActionMenu>`. Closes the menu after activation. */
export function DSActionMenuItem({
  onSelect,
  destructive = false,
  disabled = false,
  active = false,
  dataTestId,
  children,
}: DSActionMenuItemProps) {
  const ctx = useContext(DSActionMenuContext);
  const colorClasses = disabled
    ? "cursor-not-allowed opacity-50"
    : destructive
      ? "text-danger hover:bg-tertiary"
      : active
        ? "text-brand hover:bg-tertiary"
        : "text-primary hover:bg-tertiary";
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      aria-current={active ? "true" : undefined}
      onClick={() => {
        if (disabled) return;
        onSelect();
        ctx?.close();
      }}
      data-testid={dataTestId}
      className={`block w-full px-4 py-2 text-left text-sm transition-colors ${colorClasses}`}
    >
      {children}
    </button>
  );
}
